import React, { useRef, useEffect, useCallback, useState } from 'react';
import { DrawingEngine } from './DrawingEngine';
import { Ellipse } from './components/shapes/Ellipse';
import { Spline } from './components/shapes/Spline';
// import { Poly } from './components/shapes/Poly';
// import { Curve } from './components/shapes/Curve';
import { Rectangle } from './components/shapes/Rectangle';
import { Circle } from './components/shapes/Circle';
import { Square } from './components/shapes/Square';
import { PolySpline } from './components/shapes/PolySpline';
import { CrossArea } from './components/shapes/CrossArea';

export const GridArea = React.forwardRef(({
    gridWidth,
    gridHeight,
    canvasWidth,
    canvasHeight,
    spacing = 4,
    transform,
    mode,
    color,
    fillColor,
    className = "",
    gridOffset = { x: 100, y: 100 },
    onGridOffsetChange,
    onSplinePointsChange,
    fillProps,
    onSelectionChange,
    isThreadLoaded,
    showEntryPoint = false,
    currentStitchIndex = 0,
    setTotalStitches,
    setStitchColorMap,
    crossAreaParams = null,
}, ref) => {

    const canvasRef = useRef(null);
    const drawingEngineRef = useRef(new DrawingEngine());
    const creatingObjectRef = useRef(null);
    const [graphicObjects, setGraphicObjects] = useState([]);
    const [draggingControlPoint, setDraggingControlPoint] = useState(null);
    const [draggingObject, setDraggingObject] = useState(null);
    const [selectionChanged, setSelectionChanged] = useState(false);

    // Calculate total stitches and color map
    useEffect(() => {
        let total = 0;
        const colorMap = [];
        graphicObjects.forEach(obj => {
            if (obj.stitchLists) {
                obj.stitchLists.forEach(list => {
                    if (list.length > 0) {
                        const color = list.color || obj.fillColor || obj.color;
                        colorMap.push({ color, length: list.length });
                        total += list.length;
                    }
                });
            }
        });
        if (setTotalStitches) setTotalStitches(total);
        if (setStitchColorMap) setStitchColorMap(colorMap);
    }, [graphicObjects, setTotalStitches, setStitchColorMap, selectionChanged, color, fillColor, currentStitchIndex]);


    let obj = null;

    const createNewObject = () => {
        const { type = 'none', spacing = 2, angle = 45, segmentLength = 5 } = fillProps || {};
        switch (mode) {
            case 'rectangle': obj = new Rectangle(0, 0, 0, 0, color, fillColor, type, spacing, angle, segmentLength); break;
            case 'square': obj = new Square(0, 0, 0, color, fillColor, type, spacing, angle, segmentLength); break;
            case 'circle': obj = new Circle(0, 0, 0, color, fillColor, type, spacing, angle, segmentLength); break;
            case 'ellipse': obj = new Ellipse([], color, fillColor, type, spacing, angle, segmentLength); break;
            case 'spline': obj = new Spline([], color, fillColor, type, spacing, angle, segmentLength); break;
            case 'polyspline': obj = new PolySpline([], color, fillColor, type, spacing, angle, segmentLength); break;
            case 'poly':
                // PolySpline but forced to lines? No, just empty PolySpline, we handle points in AddPoint
                obj = new PolySpline([], color, fillColor, type, spacing, angle, segmentLength);
                break;
            case 'curve': obj = new PolySpline([], color, fillColor, fillProps.type, fillProps.spacing, fillProps.angle, fillProps.segmentLength, 0.5); break;
            case 'crossarea':
                // CrossArea is created via dialog params and placed directly via handleCrossAreaConfirm.
                // Don't create a placeholder object here - it will be created and added directly in WorkArea.
                obj = null;
                break;
            default: obj = null; break;
        }
        creatingObjectRef.current = obj;
    }

    useEffect(() => {
        createNewObject();
    }, [mode, color, fillColor, fillProps, crossAreaParams]);

    // Sync fill properties to selected objects when they change in the ribbon
    useEffect(() => {
        const selectedObjects = graphicObjects.filter(o => o.isSelected);
        if (selectedObjects.length > 0) {
            selectedObjects.forEach(obj => {
                if (fillProps) {
                    obj.fillType = fillProps.type;
                    obj.spacing = fillProps.spacing;
                    obj.angle = fillProps.angle;
                    obj.segmentLength = fillProps.segmentLength;
                }
                if (fillColor !== undefined) {
                    obj.fillColor = fillColor;
                }
            });
            // Trigger a re-render to show updated fill properties
            setSelectionChanged(prev => !prev);
        }
    }, [fillProps, fillColor, graphicObjects]);

    // Clamp coordinates to grid bounds
    const clampToGrid = useCallback((gridX, gridY) => {
        return {
            x: Math.max(0, Math.min(gridWidth, gridX)),
            y: Math.max(0, Math.min(gridHeight, gridY))
        };
    }, [gridWidth, gridHeight]);

    // Convert world coordinates to grid-local coordinates
    const worldToGrid = useCallback((worldX, worldY) => {
        return {
            x: worldX - gridOffset.x,
            y: worldY - gridOffset.y
        };
    }, [gridOffset]);

    const handleSelection = useCallback((worldX, worldY, event) => {
        const { x: gridX, y: gridY } = worldToGrid(worldX, worldY);
        let selectionHasChanged = false; // Flag to track if selection has changed

        // If shift key is not pressed, clear all previous selections
        if (!event.shiftKey) {
            graphicObjects.forEach(obj => {
                if (obj.isSelected) {
                    obj.isSelected = false;
                    selectionHasChanged = true; // Selection has changed
                }
            });
        }

        let clickedObject = null;
        // Iterate backwards to select the top-most object first
        for (let i = graphicObjects.length - 1; i >= 0; i--) {
            const obj = graphicObjects[i];
            // Check if the object has a hit-test method and if the click is inside
            if (obj.isPointInside && obj.isPointInside(gridX, gridY, transform)) {
                clickedObject = obj;
                break; // Found the object, no need to check further
            }
        }

        if (clickedObject) {
            // Toggle the selection state of the clicked object
            clickedObject.isSelected = !clickedObject.isSelected;
            selectionHasChanged = true; // Selection has changed
        }

        // Trigger a re-render only if the selection has actually changed
        if (selectionHasChanged) {
            setSelectionChanged(prev => !prev); // Toggle state to trigger effect

            // Notify parent about selection change
            if (onSelectionChange) {
                // Find all selected objects
                const selected = graphicObjects.filter(o => o.isSelected);
                // Return all selected objects
                onSelectionChange(selected);
            }
        }

    }, [graphicObjects, worldToGrid, transform, onSelectionChange]);

    // Expose methods to parent component
    React.useImperativeHandle(ref, () => ({
        getCanvas: () => canvasRef.current,
        getContext: () => canvasRef.current?.getContext('2d'),
        getGraphicObjects: () => graphicObjects,
        clearDrawings: () => {
            setGraphicObjects([]);
        },
        getDimensions: () => ({ width: gridWidth, height: gridHeight }),
        getGridOffset: () => gridOffset,
        isPointInBounds: (worldX, worldY) => {
            return worldX >= gridOffset.x &&
                worldX <= gridOffset.x + gridWidth &&
                worldY >= gridOffset.y &&
                worldY <= gridOffset.y + gridHeight;
        },
        handleMouseDown: (worldX, worldY, event) => handleGridMouseDown(worldX, worldY, event),
        handleMouseUp: (worldX, worldY, event) => handleGridMouseUp(worldX, worldY, event),
        handleMouseMove: (worldX, worldY, event) => handleGridMouseMove(worldX, worldY, event),
        // Expose new selection handler
        handleSelection: (worldX, worldY, event) => handleSelection(worldX, worldY, event),
        // Expose method to finish current object creation
        finishCurrentObject: () => {
            const currentObject = creatingObjectRef.current;
            if (currentObject && currentObject.mouseUp && currentObject.mouseUp()) {
                setGraphicObjects(prev => [...prev, currentObject]);
                createNewObject();
                redrawCanvas();
                return true;
            }
            return false;
        },
        // Force update method if needed
        forceUpdate: () => setSelectionChanged(prev => !prev),
        redraw: () => redrawCanvas(),
        modifyObjects: (toRemove, toAdd) => {
            console.log('GridArea: modifyObjects called');
            console.log('toRemove:', toRemove);
            console.log('toAdd:', toAdd);
            setGraphicObjects(prev => {
                const newObjects = prev.filter(obj => !toRemove.includes(obj));
                if (toAdd) {
                    if (Array.isArray(toAdd)) {
                        newObjects.push(...toAdd);
                    } else {
                        newObjects.push(toAdd);
                    }
                }
                console.log('newObjects after modify:', newObjects);
                return newObjects;
            });
            // Clear selection after modification or handle it? 
            // Usually valid to clear or select the new object.
            // Let's clear for safety/simplicity first.
            setSelectionChanged(prev => !prev);
        },
        addGraphicObject: (obj) => {
            setGraphicObjects(prev => [...prev, obj]);
            setSelectionChanged(prev => !prev);
        },
        handleCrossInteraction: (gridX, gridY, tool, colorIndexToPaint = null) => handleCrossInteraction(gridX, gridY, tool, colorIndexToPaint),
        clearDraggingState: () => {
            setDraggingControlPoint(null);
            setDraggingObject(null);
        }
    }));

    // Grid drawing logic
    const drawGrid = useCallback((ctx, drawingEngine) => {
        drawingEngine.drawGrid(ctx, gridOffset, gridWidth, gridHeight, spacing, transform.scale);
    }, [gridWidth, gridHeight, spacing, transform.scale, gridOffset]);

    // Background drawing
    const drawBackground = useCallback((ctx, drawingEngine) => {
        drawingEngine.drawBackground(ctx, gridOffset, gridWidth, gridHeight, spacing, transform.scale);
    }, [gridWidth, gridHeight, gridOffset, transform.scale]);

    // Apply transformation to context
    const applyTransform = useCallback((ctx) => {
        ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transformation
        ctx.translate(transform.translateX, transform.translateY);
        ctx.scale(transform.scale, transform.scale);
    }, [transform]);

    // Main redraw function
    const redrawCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!ctx || !canvas) return;

        const drawingEngine = drawingEngineRef.current;

        // Clear the entire canvas
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Apply transform for drawing
        applyTransform(ctx);

        // Draw grid area background and grid
        drawBackground(ctx, drawingEngine);
        drawGrid(ctx, drawingEngine);

        // Render all graphic objects (adjust for grid offset)
        ctx.save();
        ctx.translate(gridOffset.x, gridOffset.y);

        // Set clipping region to grid bounds
        ctx.beginPath();
        ctx.rect(0, 0, gridWidth, gridHeight);
        ctx.clip();

        // The renderDrawing method on each object will handle the color based on isSelected
        let stitchOffset = 0;
        graphicObjects.forEach(obj => {
            obj.renderDrawing(ctx, transform, currentStitchIndex, stitchOffset);
            if (obj.stitchLists) {
                obj.stitchLists.forEach(list => stitchOffset += list.length);
            }
            if (obj.isSelected && obj.renderActivePoints) {
                obj.renderActivePoints(ctx, transform);
            }
        });

        const currentObject = creatingObjectRef.current;
        if (currentObject) {
            // Don't draw two-click shapes until second point is placed
            const isTwoClickShape = ['rectangle', 'square', 'circle'].includes(mode);
            const isComplete = currentObject.isComplete?.() ?? true;
            
            if (!isTwoClickShape || !currentObject.isDrawing() || isComplete) {
                currentObject.renderDrawing(ctx, transform, currentStitchIndex, stitchOffset);
                if (currentObject.renderActivePoints) {
                    currentObject.renderActivePoints(ctx, transform);
                }
            } else if (isTwoClickShape && currentObject.isDrawing() && !isComplete) {
                // Only draw active point(s) for two-click shapes waiting for second click
                if (currentObject.renderActivePoints) {
                    currentObject.renderActivePoints(ctx, transform);
                }
            }
        }

        // Draw entry point markers if enabled
        if (showEntryPoint) {
            ctx.save();

            // Draw small filled green circles for every stitch point (twice smaller than entry markers)
            ctx.fillStyle = 'green';
            const smallRadius = 0.25; // half of entry radius (0.5)

            graphicObjects.forEach(obj => {
                if (!obj.stitchLists || obj.stitchLists.length === 0) return;
                obj.stitchLists.forEach(list => {
                    if (!list || list.length === 0) return;
                    for (let i = 0; i < list.length; i++) {
                        const p = list[i];
                        ctx.beginPath(); ctx.arc(p.x, p.y, smallRadius, 0, Math.PI * 2); ctx.fill();
                    }
                });
            });

            if (creatingObjectRef.current && creatingObjectRef.current.stitchLists) {
                creatingObjectRef.current.stitchLists.forEach(list => {
                    if (!list || list.length === 0) return;
                    for (let i = 0; i < list.length; i++) {
                        const p = list[i];
                        ctx.beginPath(); ctx.arc(p.x, p.y, smallRadius, 0, Math.PI * 2); ctx.fill();
                    }
                });
            }

            // Draw entry point circles (outline, larger)
            ctx.strokeStyle = 'red';
            // Use world-unit stroke width so it scales with zoom
            ctx.lineWidth = 0.2;

            // Use a fixed radius in world units (0.5) so the markers grow when zooming in
            const radiusWorld = 0.5;

            graphicObjects.forEach(obj => {
                if (!obj.stitchLists || obj.stitchLists.length === 0) return;
                obj.stitchLists.forEach(list => {
                    if (!list || list.length === 0) return;
                    const start = list[0];
                    const end = list[list.length - 1];
                    ctx.beginPath(); ctx.arc(start.x, start.y, radiusWorld, 0, Math.PI * 2); ctx.stroke();
                    ctx.beginPath(); ctx.arc(end.x, end.y, radiusWorld, 0, Math.PI * 2); ctx.stroke();
                });
            });

            // Also include the current creating object if it has stitches
            if (creatingObjectRef.current && creatingObjectRef.current.stitchLists) {
                creatingObjectRef.current.stitchLists.forEach(list => {
                    if (!list || list.length === 0) return;
                    const start = list[0];
                    const end = list[list.length - 1];
                    ctx.beginPath(); ctx.arc(start.x, start.y, radiusWorld, 0, Math.PI * 2); ctx.stroke();
                    ctx.beginPath(); ctx.arc(end.x, end.y, radiusWorld, 0, Math.PI * 2); ctx.stroke();
                });
            }

            ctx.restore();
        }

        ctx.restore();
    }, [graphicObjects, transform, applyTransform, drawBackground, drawGrid, gridOffset, gridWidth, gridHeight, selectionChanged, canvasWidth, canvasHeight, isThreadLoaded, showEntryPoint, currentStitchIndex]);


    // Handle mouse down within grid area
    const handleGridMouseDown = useCallback((worldX, worldY, event) => {
        const { x: gridX, y: gridY } = worldToGrid(worldX, worldY);
        const tolerance = 15 / transform.scale;

        if (mode === 'select') {
            // First, check for control point clicks
            for (let i = graphicObjects.length - 1; i >= 0; i--) {
                const obj = graphicObjects[i];
                if (obj.isSelected && obj.getControlPoints) {
                    const controlPoints = obj.getControlPoints();
                    for (let j = 0; j < controlPoints.length; j++) {
                        const cp = controlPoints[j];
                        const dist = Math.sqrt(Math.pow(gridX - cp.x, 2) + Math.pow(gridY - cp.y, 2));
                        if (dist < tolerance) {
                            setDraggingControlPoint({ object: obj, pointIndex: j, startX: gridX, startY: gridY });
                            return; // Stop further processing
                        }
                    }
                }
            }

            // If no control point was clicked, check for object selection/dragging
            let clickedObject = null;
            for (let i = graphicObjects.length - 1; i >= 0; i--) {
                const obj = graphicObjects[i];
                if (obj.isPointInside && obj.isPointInside(gridX, gridY, transform)) {
                    clickedObject = obj;
                    break;
                }
            }

            if (clickedObject && clickedObject.isSelected) {
                // If a selected object is clicked, start dragging it
                setDraggingObject({ object: clickedObject, startX: gridX, startY: gridY });
            } else {
                // Otherwise, handle selection
                handleSelection(worldX, worldY, event);
            }
            return;
        }

        // Original object creation logic
        const clampedPoint = clampToGrid(gridX, gridY);
        const currentObject = creatingObjectRef.current;

        // For two-click creation (rectangle, square, circle)
        if (currentObject && currentObject.isDrawing() && ['rectangle', 'square', 'circle'].includes(mode)) {
            // Second click - complete the shape
            currentObject.AddSecondPoint(clampedPoint);
            setGraphicObjects(prev => [...prev, currentObject]);
            createNewObject();
            redrawCanvas();
            return;
        }
        
        // For CrossArea: single click to place (size from dialog)
        if (currentObject && currentObject.isDrawing() && mode === 'crossarea') {
            currentObject.AddSecondPoint(clampedPoint);
            setGraphicObjects(prev => [...prev, currentObject]);
            createNewObject();
            redrawCanvas();
            return;
        }

        // strict line mode for 'poly' tool
        // otherwise follow key modifiers (ctrl: line, shift: interpolative)
        let isLine = event.ctrlKey;
        let isInterpolative = event.shiftKey;

        if (mode === 'poly') {
            isLine = true;
            isInterpolative = false;
        } else if (mode === 'curve') {
            isLine = false;
            isInterpolative = true;
        } else if (mode === 'polyspline') {
            if (event.button === 0) { // Left click
                isLine = false;
                isInterpolative = true;
            } else if (event.button === 2) { // Right click
                isLine = true;
                isInterpolative = false;
            }
        }

        if (currentObject.AddPoint(clampedPoint, isLine, isInterpolative)) {
            setGraphicObjects(prev => [...prev, currentObject]);
            createNewObject();
        }
        redrawCanvas();

    }, [worldToGrid, clampToGrid, mode, color, redrawCanvas, graphicObjects, handleSelection, transform, fillColor, fillProps]);

    // Handle cross stitch specific interactions
    const handleCrossInteraction = useCallback((gridX, gridY, tool, colorIndexToPaint = null) => {
        const selectedObjects = graphicObjects.filter(o => o.isSelected && o instanceof CrossArea);
        if (selectedObjects.length === 0) return;

        let changed = false;
        let pickedColorIndex = -1; // Default to -1 indicating no color picked or invalid

        selectedObjects.forEach(obj => {
            const cell = obj.getCellAt(gridX, gridY);
            if (cell) {
                if (tool === 'paint') {
                    // Allow painting with any palette index (including 0 for erase)
                    if (typeof colorIndexToPaint === 'number') {
                        obj.paintCell(cell.row, cell.col, colorIndexToPaint);
                        changed = true;
                    }
                } else if (tool === 'erase') {
                    // Explicitly set cell to 0 (empty)
                    obj.paintCell(cell.row, cell.col, 0);
                    changed = true;
                } else if (tool === 'pick') {
                    pickedColorIndex = obj.cells[cell.row][cell.col];
                    // We don't change 'changed' here as picking doesn't modify the object
                }
            }
        });

        if (changed) {
            redrawCanvas();
        }
        return pickedColorIndex;
    }, [graphicObjects, gridOffset, redrawCanvas]);

    // Handle mouse up - can be called even if mouse is outside grid
    const handleGridMouseUp = useCallback((worldX, worldY, event) => {
        if (draggingControlPoint) {
            setDraggingControlPoint(null);
            return;
        }

        if (draggingObject) {
            setDraggingObject(null);
            return;
        }

        const currentObject = creatingObjectRef.current;
        if (!currentObject) return;

        if (event.button === 2) {
            if (mode === 'polyspline') {
                // Right click no longer finishes polyspline
                return;
            }
            if (currentObject.mouseUp && currentObject.mouseUp()) {
                setGraphicObjects(prev => [...prev, currentObject]);
                createNewObject();
            }
            return;
        }

        // Two-click shapes are now completed in handleGridMouseDown, not here
    }, [worldToGrid, clampToGrid, mode, color, gridOffset, gridWidth, gridHeight, draggingControlPoint, draggingObject, graphicObjects, fillColor, redrawCanvas, createNewObject]);

    // Handle mouse move - can be called even if mouse is outside grid
    const handleGridMouseMove = useCallback((worldX, worldY, event) => {
        const { x: gridX, y: gridY } = worldToGrid(worldX, worldY);

        if (draggingControlPoint) {
            const { object, pointIndex } = draggingControlPoint;
            object.updateControlPoint(pointIndex, gridX, gridY);
            redrawCanvas();
            return;
        }

        if (draggingObject) {
            const { startX, startY } = draggingObject;
            const dx = gridX - startX;
            const dy = gridY - startY;

            // Move all selected objects
            graphicObjects.forEach(obj => {
                if (obj.isSelected) {
                    obj.move(dx, dy);
                }
            });

            // Update the start position for the next move event
            setDraggingObject(prev => ({ ...prev, startX: gridX, startY: gridY }));
            redrawCanvas();
            return;
        }

        // For two-click creation (rectangle, square, circle), don't update on mouse move
        const currentObject = creatingObjectRef.current;
        if (!currentObject || !currentObject.isDrawing() || ['rectangle', 'square', 'circle'].includes(mode)) return;

        const clampedPoint = clampToGrid(gridX, gridY);
        currentObject.AddSecondPoint(clampedPoint);
        redrawCanvas();

    }, [mode, worldToGrid, gridOffset, gridWidth, gridHeight, clampToGrid, redrawCanvas, draggingControlPoint, draggingObject, graphicObjects]);


    useEffect(() => {
        redrawCanvas();
    }, [redrawCanvas]);

    return (
        <canvas
            ref={canvasRef}
            width={canvasWidth}
            height={canvasHeight}
            className={`grid-area ${className}`}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                pointerEvents: 'none', // Let parent handle events
                zIndex: 1
            }}
        />
    );
});

GridArea.displayName = 'GridArea';